import { Box, ColorOptions, LoaderNew, ModBox, TextNew } from '@jfs/ui-new'
import { FC, memo, useEffect, useRef, useState } from 'react'
import { InteractionManager } from 'react-native'
import { useLazyApiData, useStores } from '../../data/store'
import { refundTheme } from '../../util/customTheme'
import { AepsTextInput } from '../AepsTextInput'
import styles from './styles'

const ERROR_COLOR = refundTheme.colors.ERROR as ColorOptions
const COUNTDOWN = 30
const SECOND = 1000
const MAX_INPUT = 6
const MAX_RETRY = 3

interface DeleteBeneficiaryOtpProps {
    mobileNumber: string
    value?: string
    onChangeText?: (text: string) => void
    onResendOtpButtonPress?: () => void
    hide?: boolean
    mode?: number
    purpose?: number
    countdownTime?: number
}

const DeleteBeneficiaryOtp: FC<DeleteBeneficiaryOtpProps> = memo((props) => {
    const { mobileNumber, hide, onChangeText, onResendOtpButtonPress, mode, purpose, countdownTime = 30 } = props

    const [otpValue, setOtpValue] = useState(props?.value ?? '')
    const [focused, setFocused] = useState(false)
    const [countDownTimer, setCountDownTimer] = useState(countdownTime)
    const [error, setError] = useState('')
    const totalResendPresses = useRef(0)
    const OtpInput = useRef<any>(null)

    const { sendOtp } = useStores((store) => ({
        sendOtp: store.moneyTransferSession.sendOtp,
    }))

    const [lazySendOtp, sendOtpHandler] = useLazyApiData(sendOtp as any)

    const isResendOtpLoading = sendOtpHandler?.state === 'pending'

    // Timer logic
    useEffect(() => {
        let timer: any
        if (countDownTimer > 0) {
            timer = setInterval(() => setCountDownTimer((prev) => prev - 1), SECOND)
        }
        return () => clearInterval(timer)
    }, [countDownTimer])

    // Autofocus
    useEffect(() => {
        InteractionManager.runAfterInteractions(() => {
            OtpInput?.current?.focus?.()
        })
    }, [])

    const handleChange = (text: string) => {
        setOtpValue(text)
        onChangeText?.(text)
    }

    const onFocus = () => setFocused(true)
    const onBlur = () => setFocused(false)

    const handleResendOtp = () => {
        if (onResendOtpButtonPress) {
            onResendOtpButtonPress()
        } else if (mobileNumber?.length >= 10) {
            lazySendOtp(mobileNumber, { mode: 33, purpose: 19 })
        }
        setCountDownTimer(countdownTime)
        setOtpValue('')
        totalResendPresses.current += 1
    }

    const ResendComponent = () => {
        const maxResendReached = totalResendPresses.current >= MAX_RETRY
        if (isResendOtpLoading) {
            return <LoaderNew type="small" color="PRIMARY" />
        } else if (countDownTimer > 0 && countDownTimer <= countdownTime) {
            return <TextNew {...styles.timerText} title={`00:${countDownTimer}`} />
        } else {
            return !maxResendReached ? (
                <ModBox flexDirection="row" onPress={handleResendOtp}>
                    <TextNew {...styles.resendText} title="RESEND" />
                    <TextNew {...styles.countText} title={` (${MAX_RETRY - totalResendPresses.current})`} />
                </ModBox>
            ) : null
        }
    }

    if (hide) return null

    const showError = otpValue.length < MAX_INPUT && !focused && otpValue.length > 0

    return (
        <Box {...styles.container} onPress={() => OtpInput?.current?.focus?.()} underlayColor="TRANSPARENT">
            <Box {...styles.subContainer}>
                <Box {...styles.wrap} underlayColor="TRANSPARENT">
                    <Box {...styles.inputBox}>
                        <AepsTextInput
                            value={otpValue}
                            onChangeText={handleChange}
                            placeholder="6 Digit OTP"
                            secureTextEntry
                            onFocus={onFocus}
                            onBlur={onBlur}
                            rightComponent={<ResendComponent />}
                            keyboardType="numeric"
                            maxLength={6}
                            color={showError ? ERROR_COLOR : undefined}
                            tintColor={showError ? ERROR_COLOR : (refundTheme.colors.PRIMARY as ColorOptions)}
                            bottomLineColor={showError ? ERROR_COLOR : (refundTheme.colors.PRIMARY as ColorOptions)}
                            placeholderTextColor={showError ? ERROR_COLOR : undefined}
                            isError={showError}
                            returnKeyType={otpValue.length === 6 ? 'done' : 'next'}
                            ref={OtpInput}
                        />
                    </Box>
                </Box>
            </Box>
            {showError && <Box width={'100%'} marginTop={5}><TextNew {...styles.errorText}>{error || 'Invalid OTP'}</TextNew></Box>}
        </Box>
    )
})

export { DeleteBeneficiaryOtp }

